import { useMemo } from "react";
import { MutateOptions, useMutation, UseMutationOptions } from "@tanstack/react-query";
import { UpdateCardQuery } from "../../query.types";
import { SavedAirResponseDataDTO } from "../../response.types";

import { AirSavedCardModel } from "../../../model/model";
import { savedShipmentService } from "../../api";

type UpdateCardResponse = SavedAirResponseDataDTO;
type UpdateCardError = Error;
export type UpdateCardFunc = (
  data: UpdateCardQuery,
  options?: Omit<MutateOptions<UpdateCardResponse, UpdateCardError, UpdateCardQuery>, "onSuccess" | "onSettled"> & {
    onSuccess?: (data: AirSavedCardModel) => void;
    onSettled?: (data: AirSavedCardModel | undefined, error: Error | null, variables: UpdateCardQuery) => void;
  }
) => void;

type UseUpdateSingleCardMutationReturnType = {
  updateCard: UpdateCardFunc;
  updatedCard: AirSavedCardModel | undefined;
  isCardUpdateSuccess: boolean;
  isCardUpdateError: boolean;
  isCardUpdateLoading: boolean;
};

export const useUpdateSingleCardMutation = (
  mutationOptions?: UseMutationOptions<SavedAirResponseDataDTO, Error, UpdateCardQuery>
): UseUpdateSingleCardMutationReturnType => {
  const { data, mutate, isError, isPending, isSuccess } = useMutation<
    UpdateCardResponse,
    UpdateCardError,
    UpdateCardQuery
  >({
    mutationFn: savedShipmentService.updateCard,
    ...mutationOptions,
  });

  const updateCard: UpdateCardFunc = (variables, { onSuccess, onSettled, ...rest } = {}) => {
    return mutate(variables, {
      ...rest,
      onSuccess: (data) => {
        onSuccess?.(new AirSavedCardModel(data));
      },
      onSettled: (data, error, variables) => {
        onSettled?.(data ? new AirSavedCardModel(data) : undefined, error, variables);
      },
    });
  };

  const updatedCard = useMemo<AirSavedCardModel | undefined>(() => {
    if (!data) return;
    return new AirSavedCardModel(data);
  }, [data]);

  return {
    updateCard,
    updatedCard,
    isCardUpdateSuccess: isSuccess,
    isCardUpdateError: isError,
    isCardUpdateLoading: isPending,
  };
};
